import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { isTransactable, listAccounts } from '@/api/accounts'
import { createCashEntry, listContacts, type Contact } from '@/api/people'
import { DatePicker } from '@/components/DatePicker'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Radio } from '@/components/ui/radio'
import { Select } from '@/components/ui/select'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { today } from '@/lib/transactionDateRangeFilter'
import { ROUTES } from '@/routes/routes'
import { MODAL_BOX, MODAL_OVERLAY } from '@/components/transactions/TransactionForms'

const LABEL_CLS = 'block text-sm font-medium text-[var(--fl-text-1)] mb-1'

type Direction = 'LENT' | 'BORROWED'

export function CashEntryModal({
  contact,
  direction,
  onClose,
}: {
  contact: Contact
  direction: Direction
  onClose: () => void
}) {
  const title =
    direction === 'LENT' ? `Lent money to ${contact.name}` : `Borrowed money from ${contact.name}`
  return (
    <div className={MODAL_OVERLAY}>
      <div className={MODAL_BOX} role="dialog" aria-label={title}>
        <h2 className="mb-4 text-lg font-bold text-[var(--fl-text-1)]">{title}</h2>
        <CashEntryForm contact={contact} initialDirection={direction} onDone={onClose} />
      </div>
    </div>
  )
}

/**
 * Lent / borrowed money with no interest: records the balance with the person. With `contact`
 * fixed (a person's page) only the money fields show; without it (Add Transaction's Lend/Borrow
 * tab) the person and direction are picked here too.
 */
export function CashEntryForm({
  contact,
  initialDirection = 'LENT',
  onDone,
}: {
  contact?: Contact
  initialDirection?: Direction
  onDone: () => void
}) {
  const { format } = useFormatCurrency()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [direction, setDirection] = useState<Direction>(initialDirection)
  const [contactId, setContactId] = useState(contact?.id ?? '')
  const [amountText, setAmountText] = useState('')
  const [accountId, setAccountId] = useState('')
  const [alreadyOwed, setAlreadyOwed] = useState(false)
  const [date, setDate] = useState(today())
  const [description, setDescription] = useState('')
  const [amountError, setAmountError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: accountsData } = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() })
  const accounts = (accountsData?.accounts ?? []).filter((a) => isTransactable(a.type))
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', { includeArchived: false }],
    queryFn: () => listContacts(),
    enabled: !contact,
  })
  const personName = contact?.name ?? contacts.find((c) => c.id === contactId)?.name

  const mutation = useMutation({
    mutationFn: (amount: number) =>
      createCashEntry({
        contactId,
        direction,
        amount,
        ...(alreadyOwed ? { alreadyOwed: true } : { financialAccountId: accountId }),
        date,
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: (data) => {
      for (const key of [
        'contacts',
        'owed-summary',
        'transactions',
        'accounts',
        'dashboard',
        'budget',
      ])
        queryClient.invalidateQueries({ queryKey: [key] })
      if (data?.earmarkWarning) {
        const goal = data.earmarkWarning.goals[0]
        toast.warning(`${goal.name} is now short by ${format(data.earmarkWarning.shortfall)}`, {
          action: {
            label: 'View goal',
            onClick: () => navigate(ROUTES.APP.GOAL_DETAIL(goal.id)),
          },
        })
      }
      onDone()
    },
    onError: (err) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setSubmitError(detail ?? 'Something went wrong. Please try again.')
    },
  })

  function submit() {
    setSubmitError(null)
    const trimmed = amountText.trim()
    const parsed = Number(trimmed)
    if (!trimmed || Number.isNaN(parsed) || toPaise(parsed) <= 0) {
      setAmountError('Enter an amount greater than ₹0')
      return
    }
    setAmountError(null)
    mutation.mutate(fromPaise(toPaise(parsed)))
  }

  return (
    <div className="space-y-4">
      {!contact && (
        <>
          <fieldset className="flex gap-6">
            <legend className={LABEL_CLS}>Direction</legend>
            {(
              [
                ['LENT', 'I lent money'],
                ['BORROWED', 'I borrowed money'],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                className="flex items-center gap-2 text-sm text-[var(--fl-text-1)]"
              >
                <Radio
                  name="cash-direction"
                  value={value}
                  checked={direction === value}
                  onChange={() => setDirection(value)}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <div>
            <label className={LABEL_CLS}>Person</label>
            <Select aria-label="Person" value={contactId} onChange={setContactId}>
              <option value="">Select person</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            {contacts.length === 0 && (
              <p className="mt-1 text-xs text-[var(--fl-text-2)]">
                Add the person on the People page first.
              </p>
            )}
          </div>
        </>
      )}
      <div>
        <label htmlFor="cash-amount" className={LABEL_CLS}>
          Amount
        </label>
        <Input
          id="cash-amount"
          inputMode="decimal"
          value={amountText}
          onChange={(e) => setAmountText(e.target.value)}
        />
        {amountError && <p className="mt-1 text-xs text-[var(--fl-error)]">{amountError}</p>}
      </div>
      <label className="flex items-start gap-2 text-sm text-[var(--fl-text-1)]">
        <Checkbox checked={alreadyOwed} onChange={(e) => setAlreadyOwed(e.target.checked)} />
        <span>Already owed before I started using LogMySpends</span>
      </label>
      {alreadyOwed ? (
        <p className="text-xs text-[var(--fl-text-2)]">
          No account is used. This only records the balance
          {personName ? ` with ${personName}` : ''}.
        </p>
      ) : (
        <div>
          <label className={LABEL_CLS}>Account</label>
          <Select aria-label="Account" value={accountId} onChange={setAccountId}>
            <option value="">Select account</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </div>
      )}
      <div>
        <label className={LABEL_CLS}>Date</label>
        <DatePicker value={date} onChange={setDate} />
      </div>
      <div>
        <label htmlFor="cash-description" className={LABEL_CLS}>
          Description (optional)
        </label>
        <Input
          id="cash-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <p className="text-xs text-[var(--fl-text-2)]">
        No interest. For a loan with interest or a due date, use Loans.
      </p>
      {submitError && <p className="text-xs text-[var(--fl-error)]">{submitError}</p>}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onDone} className="flex-1">
          Cancel
        </Button>
        <Button
          onClick={submit}
          disabled={contactId === '' || (!alreadyOwed && accountId === '') || mutation.isPending}
          className="flex-1 font-semibold"
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  )
}
